<template>
  <div class="infoWindowHeader">
    <InfoWindowName
      :info-window-name="infoWindowContent.title"
      :editing="infoWindowContent.editing"
      :placeholder="placeholder"
      @updateName="(name) => infoWindowContent.updateName(name)"
    />
  </div>
  <div class="infoWindowBody">
    <DisplayFields
      v-if="infoWindowContent.displayFields.length || infoWindowContent.editing"
      :display-fields="infoWindowContent.displayFields"
      :editing="infoWindowContent.editing"
      @addDisplayField="(...args) => infoWindowContent.addDisplayField(...args)"
      @updateDisplayField="(...args) => infoWindowContent.updateDisplayField(...args)"
      @deleteDisplayField="(...args) => infoWindowContent.deleteDisplayField(...args)"
    />
    <div v-if="showAddToOverlayButton">
      <np-action-btn
        :disabled="!selectedOverlay"
        data-testid="add-to-overlay-button"
        class="float-right"
        @click="addToOverlay"
      >
        Add to Overlay
      </np-action-btn>
    </div>
    <div v-if="infoWindowContent.comps">
      <div
        v-if="infoWindowContent.comps.length"
        class="comp-window-spacer"
      />
      <CompGroups
        :comps="infoWindowContent.comps"
        :editing="infoWindowContent.editing"
        data-testid="compGroups"
        :info-window-content="infoWindowContent"
        @addComp="(...args) => infoWindowContent.addComp(...args)"
        @addCompField="(...args) => infoWindowContent.addCompDisplayField(...args)"
        @updateCompField="(...args) => infoWindowContent.updateCompDisplayField(...args)"
        @updateCompName="(...args) => infoWindowContent.updateCompName(...args)"
        @deleteCompField="(...args) => infoWindowContent.deleteCompDisplayField(...args)"
        @deleteComp="(...args) => handleDeleteComp(...args)"
      />
    </div>
    <div v-if="infoWindowContent.editing && infoWindowContent.featureType === 'placeMarker'">
      <AddCompButtons
        data-testid="addCompButtons"
        @addComp="(...args) => infoWindowContent.addComp(...args)"
      />
    </div>
  </div>
</template>

<script>
import {mapState, mapMutations} from 'vuex';

import {featureOptions} from '@/services/OverlaysService';
import InfoWindowName from './sections/Header/InfoWindowName.vue';
import DisplayFields from './DisplayFields/DisplayFields.vue';
import CompGroups from './DisplayFields/Comps/CompGroups.vue';
import AddCompButtons from './DisplayFields/Comps/AddCompButtons.vue';
import {OverlayService} from '@/services/OverlaysService';

export default {
  name: 'FeatureInfoWindow',
  components: {
    InfoWindowName,
    DisplayFields,
    CompGroups,
    AddCompButtons,
  },
  props: {
    /** @type {import('@/lib/classes/InfoWindowContent')} */
    infoWindowContent: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('infowindow', ['featureName', 'infoWindowContents']),
    ...mapState(['selectedOverlay', 'editingFeature']),
    placeholder() {
      return featureOptions.get(this.infoWindowContent.featureType)?.defaultName ?? 'Feature Name';
    },
    showAddToOverlayButton() {
      let toolboxOptions = ['route', 'polygon', 'polyline', 'label', 'blockPlan-crossDock', 'blockPlan-singleLoad'];
      const isToolboxOption = toolboxOptions.includes(this.infoWindowContent.featureType);

      const createdByToolbox = this.infoWindowContent.host?.isToolboxFeature;
      return isToolboxOption && createdByToolbox && process.env.VUE_APP_FLAVOR === 'internal';
    },
  },
  methods: {
    ...mapMutations('infowindow', ['clearInfoWindowContents']),
    ...mapMutations(['setSelectedMenuItem', 'setSnack']),

    async addToOverlay() {
      const feature = this.editingFeature;
      const toolboxOverlay = feature.parent;
      await OverlayService.copyFeatureToOverlay(feature, toolboxOverlay, this.selectedOverlay);

      this.clearInfoWindowContents();
      toolboxOverlay.deleteFeature(feature.id);
      this.setSelectedMenuItem('overlays');
    },
    async handleDeleteComp(compToDelete) {
      const compIndex = this.infoWindowContent.comps.findIndex((comp) => comp.id === compToDelete.id) || 0;
      // Remove the comp from the info window content
      this.infoWindowContent.deleteComp(compToDelete, compIndex);

      this.setSnack({
        message: 'Comp has been deleted',
        action: {
          text: 'Undo',
          callback: () => this.infoWindowContent.addComp(compToDelete, compIndex),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.comp-window-spacer {
  padding-bottom: 15px;
}
</style>
